import { queryKeys } from "~/api/cache"
import type {
  SolarLightReachApplicationDto,
  SolarLightReachDto,
  SolarLightReachOfferDto,
  SolarLightReachSelectedOfferDto,
} from "~/api/dto/solar-lightreach.dto"
import useRefreshSolarLightReachApplication from "~/api/hooks/useRefreshSolarLightReachApplication"
import useSolarLightReachContract, {
  useVoidSolarLightReachContract,
} from "~/api/hooks/useSolarLightReachContract"
import useSolarLightReachDisclosures from "~/api/hooks/useSolarLightReachDisclosures"
import useSubmitSolarLightReachApplication, {
  useReapplySolarLightReach,
} from "~/api/hooks/useSubmitSolarLightReachApplication"
import {
  isSolarLightReachPricingRequiredError,
  solarProposalErrorMessage,
} from "~/api/solar-proposal-errors"

import { LIGHTREACH_PRICING_OUT_OF_DATE } from "./solar-lightreach-copy"
import type {
  LightReachApplicationBundle,
  LightReachApplicationView,
  LightReachFinancingView,
} from "../ProposalSetup/financing/lightreach/lightreach-kind-bundle"

/**
 * Solar Proposal commands for the shared LightReach application, status and
 * contract. Solar's Refresh status is a command (the server reads LightReach
 * back), Solar can void an unsigned contract and send a new one after a
 * voided one, and LightReach expires Solar credit decisions.
 */
export const solarLightReachApplicationBundle: LightReachApplicationBundle = {
  testIdPrefix: `solar`,
  financingQueryKey: queryKeys.solarProposal.lightReach,
  useDisclosures: useSolarLightReachDisclosures,
  useSubmit: useSubmitSolarLightReachApplication,
  useContract: useSolarLightReachContract,
  isRefreshRequiredError: isSolarLightReachPricingRequiredError,
  staleOfferMessage: LIGHTREACH_PRICING_OUT_OF_DATE,
  errorMessage: solarProposalErrorMessage,
  useRefreshApplication: useRefreshSolarLightReachApplication,
  useVoidContract: useVoidSolarLightReachContract,
  reportsExpiry: true,
  resendAfterClose: true,
}

/** The same form, applying again: the earlier application stays as history. */
export const solarLightReachReapplyBundle: LightReachApplicationBundle = {
  ...solarLightReachApplicationBundle,
  useSubmit: useReapplySolarLightReach,
}

/**
 * LightReach's own word folded into the loan family the shared status reads
 * first; `providerStatus` keeps the word itself.
 */
const loanStatus = (status: string): string => {
  switch (status) {
    case `approved`:
    case `approvedWithStipulations`:
      return `approved`
    case `declined`:
    case `creditFrozen`:
      return `denied`
    case `expired`:
      return `cancelled`
    default:
      return `pending`
  }
}

/**
 * The selected offer as the shared status names it: product, term and
 * escalator. `lightReach.productId` is LightReach's own id, so it comes from
 * the stored offer the selection was re-found as; without that row the
 * status names the offer without LightReach's terms.
 */
const offerProduct = (
  offer: SolarLightReachSelectedOfferDto,
  row: SolarLightReachOfferDto | null,
): NonNullable<LightReachApplicationView[`loanProduct`]> => ({
  id: offer.loanProductId,
  name: offer.label,
  termYears: offer.termYears,
  apr: null,
  dealerFee: null,
  minimumLoanAmount: null,
  maximumLoanAmount: null,
  ...(row != null
    ? {
        lightReach: {
          productId: row.productId,
          productType: offer.productType,
          termYears: offer.termYears,
          // The shared terms read LightReach's fraction (0.0299), not a percent.
          escalationRate: row.escalationRate,
        },
      }
    : {}),
})

const toApplicationView = (
  application: SolarLightReachApplicationDto,
  offer: SolarLightReachSelectedOfferDto | null,
  row: SolarLightReachOfferDto | null,
): LightReachApplicationView => ({
  id: application.id,
  provider: `lightreach`,
  providerReference: application.providerReference,
  status: loanStatus(application.status),
  providerStatus: application.status,
  // A TPO finances no amount; the shared status never shows one.
  amount: 0,
  loanProduct: offer != null ? offerProduct(offer, row) : null,
  createdAt: application.createdAt,
  approvedOn: null,
  // The application is on the selected offer while that offer is current.
  matchesCurrentFinancing: offer?.quoteStatus === `current`,
  creditExpiresOn: application.creditExpiresOn,
  stipulations: application.stipulations,
})

/**
 * The Solar LightReach read as the shared status and contract read it. Only
 * the current application is on the selected offer; earlier ones keep their
 * outcome and date.
 */
export const toLightReachFinancingView = (
  read: SolarLightReachDto,
): LightReachFinancingView => {
  const selectedRow = read.offers.offers.find((offer) => offer.selected) ?? null
  return {
    quote:
      read.selectedOffer != null
        ? { status: read.selectedOffer.quoteStatus }
        : null,
    currentApplication:
      read.application != null
        ? toApplicationView(read.application, read.selectedOffer, selectedRow)
        : null,
    previousApplications: read.previousApplications.map((application) =>
      toApplicationView(application, null, null),
    ),
    financingLocked: read.financingLocked,
    financingLockReason: read.financingLockReason,
    lightReachContract: read.contract,
    actions: {
      startLightReachApplication: read.actions.submitApplication,
      viewLightReachApplication: {
        allowed: read.application != null,
        reason: null,
      },
      sendLightReachContract: read.actions.sendContract,
      refreshLightReachContract: read.actions.refreshContract,
      retryLightReachSupplemental: read.actions.retrySupplemental,
      reapplyLightReach: read.actions.reapply,
      voidLightReachContract: read.actions.voidContract,
      refreshLightReachApplication: read.actions.refreshApplication,
    },
  }
}
